(() => {
  const MODAL_OPEN_CLASS = 'pdp-size-chart__modal--open';
  const BODY_LOCK_CLASS = 'pdp-size-chart--locked';

  // Normalize various unit spellings to a canonical form
  /**
   * @param {string} [unit]
   * @returns {string}
   */
  const normalizeUnit = (unit) => {
    const u = (unit || 'cm').toLowerCase().trim();
    if (u === 'inch' || u === 'inches' || u === 'in') return 'in';
    if (u === 'cm' || u === 'centimeter' || u === 'centimeters') return 'cm';
    return u;
  };

  const MEASUREMENT_UNITS = new Set(['cm', 'in']);

  /**
   * @param {string} [columnHeader]
   * @returns {boolean}
   */
  const shouldSkipConversion = (columnHeader) => {
    const header = (columnHeader || '').toLowerCase();
    return header.includes('size');
  };

  /**
   * @param {number|string} value
   * @param {string} fromUnit
   * @param {string} toUnit
   * @param {string} columnKey
   * @returns {number|string}
   */
  const convertValue = (value, fromUnit, toUnit, columnKey) => {
    if (typeof value !== 'number') return value;
    if (shouldSkipConversion(columnKey)) return value;
    const from = normalizeUnit(fromUnit);
    const to = normalizeUnit(toUnit);

    if (!MEASUREMENT_UNITS.has(from) || !MEASUREMENT_UNITS.has(to)) {
      return value;
    }

    if (from === to) return value;
    if (from === 'cm' && to === 'in') {
      return value / 2.54;
    }
    if (from === 'in' && to === 'cm') {
      return value * 2.54;
    }
    return value;
  };

  /**
   * @param {number|string} value
   * @param {string} [unit]
   * @returns {string|number}
   */
  const formatDisplayValue = (value, unit) => {
    if (typeof value !== 'number') return value;
    const rounded = Math.round(value * 10) / 10;
    const str = rounded.toFixed(1);
    return str.endsWith('.0') ? str.slice(0, -2) : str;
  };

  /**
   * @typedef {Object} SizeChartData
   * @property {string[]} columns
   * @property {Object[]} rows
   * @property {string} [unit]
   */

  /**
   * @param {HTMLElement} tableEl
   * @param {SizeChartData} data
   * @param {string} unit
   * @param {string} baseUnit
   * @param {boolean} [showUnitLabel]
   */
  const renderTable = (tableEl, data, unit, baseUnit, showUnitLabel = true) => {
    if (!tableEl || !data) return;
    const columns = Array.isArray(data.columns) ? data.columns : [];
    const rows = /** @type {Record<string, any>[]} */ (Array.isArray(data.rows) ? data.rows : []);
    if (!columns.length || !rows.length) {
      tableEl.innerHTML = '';
      return;
    }

    // Expose column count to CSS for min-width calculation
    try {
      tableEl.style.setProperty('--cols', String(columns.length));
    } catch (e) { }

    let html = '<thead><tr>';
    columns.forEach((column) => {
      const displayUnitLabel = unit === 'in' ? 'in' : 'cm';
      const originalLabel = `${column}`;
      const sanitizedLabel = originalLabel.replace(/\s*\((cm|inch|in)\)\s*$/i, '').trim();
      const headerText = sanitizedLabel || originalLabel;
      const isMeasurementColumn = !shouldSkipConversion(column) && MEASUREMENT_UNITS.has(baseUnit);
      const baseLabel = `<span class="pdp-size-chart__col-label">${headerText}</span>`;
      const unitLabel = `<span class="pdp-size-chart__unit-label">(${displayUnitLabel})</span>`;
      const headerLabelHtml = isMeasurementColumn && showUnitLabel ? baseLabel + unitLabel : baseLabel;
      html += `<th>${headerLabelHtml}</th>`;
    });
    html += '</tr></thead><tbody>';

    rows.forEach((row) => {
      html += '<tr>';
      columns.forEach((column) => {
        const rawValue = row[column];
        const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        if (!Number.isNaN(numericValue) && !shouldSkipConversion(column)) {
          const converted = convertValue(numericValue, baseUnit, unit, column);
          html += `<td>${formatDisplayValue(converted, unit)}</td>`;
        } else if (!Number.isNaN(numericValue) && shouldSkipConversion(column)) {
          const displayUnit = MEASUREMENT_UNITS.has(baseUnit) ? baseUnit : 'cm';
          html += `<td>${formatDisplayValue(numericValue, displayUnit)}</td>`;
        } else {
          html += `<td>${rawValue}</td>`;
        }
      });
      html += '</tr>';
    });
    html += '</tbody>';

    tableEl.innerHTML = html;
  };

  /**
   * @param {HTMLElement} root
   */
  const initSizeChart = (root) => {
    if (!root || root.dataset.sizeChartInitialized === 'true') return;

    const trigger = /** @type {HTMLElement} */ (root.querySelector('[data-size-chart-trigger]'));
    const modal = /** @type {HTMLElement} */ (root.querySelector('[data-size-chart-modal]'));
    const content = /** @type {HTMLElement} */ (modal?.querySelector('[data-size-chart-content]'));
    if (!trigger || !modal || !content) return;

    trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-expanded') || 'false');

    const tableData = content.getAttribute('data-size-chart');
    const tableEl = /** @type {HTMLElement} */ (content.querySelector('[data-size-chart-table]'));
    const unitButtons = /** @type {NodeListOf<HTMLElement>} */ (content.querySelectorAll('[data-size-chart-unit]'));
    const slider = /** @type {HTMLElement} */ (content.querySelector('[data-size-chart-slider]'));
    const toggle = /** @type {HTMLElement} */ (content.querySelector('[data-size-chart-toggle]'));
    let parsedData = null;

    if (tableData) {
      try {
        parsedData = JSON.parse(tableData);
      } catch (error) {
        console.warn('Invalid size chart JSON', error);
      }
    }

    const baseUnit = normalizeUnit(parsedData?.unit);
    let currentUnit = baseUnit;

    const updateSliderPosition = () => {
      if (!slider || !toggle || !unitButtons.length) return;
      const buttons = Array.from(unitButtons);
      const activeIndex = Math.max(buttons.findIndex((btn) => btn.classList.contains('is-active')), 0);
      const count = Math.max(buttons.length, 2);
      const widthPercent = 100 / count;
      slider.style.width = `${widthPercent}%`;
      slider.style.transform = `translateX(${activeIndex * 100}%)`;
    };

    /**
     * @param {string} unit
     */
    const setActiveUnitButton = (unit) => {
      unitButtons.forEach((btn) => {
        const btnUnit = normalizeUnit(btn.dataset.sizeChartUnit);
        const isActive = btnUnit === unit;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });
      if (toggle) {
        toggle.dataset.activeUnit = unit;
      }
      requestAnimationFrame(updateSliderPosition);
    };

    const showUnitLabel = Boolean(toggle);

    if (parsedData && tableEl) {
      renderTable(tableEl, parsedData, currentUnit, baseUnit, showUnitLabel);
      setActiveUnitButton(currentUnit);
    }

    if (parsedData && unitButtons.length) {
      unitButtons.forEach((btn) => {
        const targetUnit = normalizeUnit(btn.dataset.sizeChartUnit);
        if (!targetUnit) return;
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          if (targetUnit === currentUnit) return;
          currentUnit = normalizeUnit(targetUnit);
          setActiveUnitButton(currentUnit);
          renderTable(tableEl, parsedData, currentUnit, baseUnit, showUnitLabel);
        });
      });
    }

    let toggleObserver = null;
    /** @type {Array<() => void>} */
    const orientationCleanup = [];
    const handleOrientationChange = () => requestAnimationFrame(updateSliderPosition);

    if (slider && unitButtons.length) {
      const updateSliderWithFrame = () => requestAnimationFrame(updateSliderPosition);
      const handleResize = () => updateSliderWithFrame();
      window.addEventListener('resize', handleResize);

      if (typeof ResizeObserver === 'function' && toggle) {
        toggleObserver = new ResizeObserver(() => updateSliderWithFrame());
        toggleObserver.observe(toggle);
      }

      if (typeof window.matchMedia === 'function') {
        const mediaQuery = window.matchMedia('(orientation: portrait)');
        if (typeof mediaQuery.addEventListener === 'function') {
          mediaQuery.addEventListener('change', handleOrientationChange);
          orientationCleanup.push(() => mediaQuery.removeEventListener('change', handleOrientationChange));
        } else if (typeof mediaQuery.addListener === 'function') {
          mediaQuery.addListener(handleOrientationChange);
          orientationCleanup.push(() => mediaQuery.removeListener(handleOrientationChange));
        }
      }

      requestAnimationFrame(updateSliderPosition);
    }

    /** @type {HTMLElement | null} */
    let lastFocused = null;

    const closeModal = () => {
      // Remove open class to trigger slide-down animation
      modal.classList.remove(MODAL_OPEN_CLASS);

      // Immediately unlock page and restore control (don't wait for animation)
      document.body.classList.remove(BODY_LOCK_CLASS);
      trigger.setAttribute('aria-expanded', 'false');
      root.classList.remove('pdp-size-chart--open');
      window.removeEventListener('keydown', handleKeydown);

      // Wait for animation to complete before hiding (faster cleanup)
      setTimeout(() => {
        modal.setAttribute('hidden', 'true');
      }, 250);

      if (toggleObserver) {
        toggleObserver.disconnect();
      }
      if (orientationCleanup.length) {
        orientationCleanup.forEach((cleanup) => {
          try {
            cleanup();
          } catch (error) {
            console.warn('Orientation listener cleanup failed', error);
          }
        });
        orientationCleanup.length = 0;
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    };

    /**
     * @param {KeyboardEvent} event
     */
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }
    };

    /**
     * @param {Event} event
     */
    const openModal = (event) => {
      event.preventDefault();
      lastFocused = /** @type {HTMLElement} */ (document.activeElement);
      modal.removeAttribute('hidden');

      // Use requestAnimationFrame to ensure CSS animation triggers
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modal.classList.add(MODAL_OPEN_CLASS);
        });
      });

      document.body.classList.add(BODY_LOCK_CLASS);
      trigger.setAttribute('aria-expanded', 'true');
      root.classList.add('pdp-size-chart--open');
      window.addEventListener('keydown', handleKeydown);
      if (toggleObserver && toggle) {
        toggleObserver.observe(toggle);
      }
      const focusTarget = /** @type {HTMLElement} */ (modal.querySelector('[data-size-chart-close]') || modal);
      focusTarget.focus({ preventScroll: true });
      requestAnimationFrame(updateSliderPosition);
    };

    trigger.addEventListener('click', openModal);

    modal.querySelectorAll('[data-size-chart-close]').forEach((/** @type {HTMLElement} */ el) => {
      el.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });

    root.dataset.sizeChartInitialized = 'true';
  };

  /**
   * @param {Document | HTMLElement} [context]
   */
  const initAllSizeCharts = (context = document) => {
    const roots = context.querySelectorAll('[data-size-chart-root]');
    roots.forEach((root) => initSizeChart(/** @type {HTMLElement} */(root)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSizeCharts(document));
  } else {
    initAllSizeCharts(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllSizeCharts(/** @type {HTMLElement} */(event.target));
  });
})();
